import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Image } from "../api/contracts/images";

interface ImageGalleryModalProps {
  images: Image[];
  isOpen: boolean;
  onClose: () => void;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  images,
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Portaled to the body: `fixed` escapes layout but not an ancestor's opacity,
  // and this opens from cards that dim themselves when the category can't be
  // taken — which washed the full-size photo out too.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-y-auto rounded-2xl bg-surface-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Close button */}
        <button
          className="sticky right-4 top-4 z-10 self-end rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
          onClick={onClose}
          aria-label="Stäng"
          style={{ position: "sticky" }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M6 6L14 14M14 6L6 14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {/* Padding is uniform: a bottom gap larger than the side gap read as a
            stray band under the last photo. The sticky close button above
            supplies the visual air at the top. */}
        <div className="flex flex-col items-center gap-6 p-4">
          {images.map((img, idx) => (
            <img
              key={idx}
              src={img.url}
              alt={img.description ?? ""}
              className="h-auto w-full rounded-xl shadow-lg"
              style={{ maxWidth: "100%" }}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
